import React, { useEffect, useState } from 'react';
import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  Illustration,
  Loader,
  ModalIsland,
  ModalPageHeader,
  Typography,
} from '@saviynt/design-system';
import { useHistory } from 'react-router-dom';

import { useExtContext } from '../../context';
import useApplyRules from '../../hooks/use-applyrules';
import useDeleteRule from '../../hooks/use-deleterule';
import useGetRules from '../../hooks/use-getrules';
import { useInvertedLocalize } from '../../utils';
import { ERROR_MESSAGE } from '../../utils/constants';
import { useLocalize } from '../../utils/localization';
import RuleTabs from '../RuleTabs/RuleTabs';

import { classificationLabels, invertedMessages } from './messages';

import './ManageClassification.css';

function ManageClassification() {
  const history = useHistory();
  const { updateExtState } = useExtContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const l = useInvertedLocalize(invertedMessages);
  const localize = useLocalize();

  const { mutate: applyRules, isPending: isApplyingRules } = useApplyRules();
  const { mutate: deleteRule, isPending: isRuleDeleting } = useDeleteRule();

  const isRuleUpdating = isApplyingRules || isRuleDeleting;

  const {
    data,
    refetch: refetchRules,
    isFetched: isRulesfetched,
    isError,
    error,
    isLoading,
    isFetching,
  } = useGetRules();

  const initialRules = data?.sort((a, b) => a.status - b.status);

  useEffect(() => {
    if (isError) {
      setErrorMessage(error.errorMessage ?? ERROR_MESSAGE);
      setShowCriticalBanner(true);
    }
  }, [isError, error]);

  const confirmedRulesCount = initialRules?.filter((rule) => rule.status === 0)
    ?.length;
  const recommendedRulesCount = initialRules?.filter(
    (rule) => rule.status === 2
  )?.length;

  const closeHandler = () => {
    history.push({ pathname: '/dashboard' });
  };

  const applyHandler = () => {
    const unConfirmedRules = initialRules?.filter((rule) => rule.status === 2);

    if (unConfirmedRules && unConfirmedRules.length > 0) {
      unConfirmedRules.forEach((rule) => {
        deleteRule({
          ...rule,
        });
      });
    }

    applyRules('', {
      onSuccess: (response) => {
        if (response.status === 200 || response.status === 201) {
          history.push({
            pathname: '/dashboard',
          });
          updateExtState('isJobTriggerSuccess', true);
        } else if (response.errorMessage || response.error) {
          setErrorMessage(response.errorMessage ?? '');
          setShowCriticalBanner(true);
        }
      },
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const closeModalHandler = () => {
    setIsOverlayOpen(false);
  };

  const ruleHeaderComp = (
    <div className='Ext-ManageClassification-modalHeader'>
      <div className='Ext-ManageClassification-modalHeader-closeBtn'>
        <ButtonIcon
          dataTestId='ModalIslandCloseButton'
          size='medium'
          kind='ghost'
          className='ModalIsland-alertCloseButton'
          icon={<Icon kind='close' color='neutral-100' size='smallMedium' />}
          onClick={closeModalHandler}
        />
      </div>
      <div className='Ext-ManageClassification-modalHeader-title'>
        <Typography
          kind='display'
          color='--color-foreground-secondary-medium'
          className='Ext-ManageClassification-modalHeader-title-text'>
          {l('Guided User Classification with Rules')}
        </Typography>
      </div>
    </div>
  );

  const ruleContentBody = (
    <div className='Ext-ManageClassification-modalContent'>
      <Typography
        kind='body1'
        className='Ext-ManageClassification-modalContent-text'>
        {l(
          'As part of the identity classification process, our system performs a comprehensive scan of all users and their attributes. Using the data gathered, we automatically generate classification rules that define and categorize user types based on their attributes.'
        )}
      </Typography>
      <Typography kind='body1-bold'>
        {l('Here is an example of a rule')}
      </Typography>
      <Illustration kind='RuleExample' />
      <Typography
        kind='body1'
        className='Ext-ManageClassification-modalContent-subText'>
        {l(
          `This rule states that 230 users were found to have an email address that includes the letters “ext” which is likely to mean “external” user.`
        )}
      </Typography>
      <div className='Ext-ManageClassification-modalContent-button'>
        <Button
          type='button'
          kind='subtle'
          size='large'
          onClick={closeModalHandler}>
          {l('Got it!')}
        </Button>
      </div>
    </div>
  );

  const confirmedRulesText = initialRules?.length
    ? l(
        'Please review classification rules and make any necessary adjustments to ensure accuracy.'
      )
    : '';

  const applyButtonLabel = localize(
    {
      id: 'external.rules.apply.count.btn',
      defaultMessage: classificationLabels['external.rules.apply.count.btn'],
    },
    { count: confirmedRulesCount, hide: () => '' }
  );
  const applyConfirmationLabel = localize(
    {
      id: 'external.rules.apply.confirmation',
      defaultMessage: classificationLabels['external.rules.apply.confirmation'],
    },
    { count: confirmedRulesCount, hide: () => '' }
  );
  const boldThis = (chunks: string) => <strong>{chunks}</strong>;
  const unconfirmedRuleText = localize(
    {
      id: 'external.rules.apply.unConfirmed',
      defaultMessage: classificationLabels['external.rules.apply.unConfirmed'],
    },
    { count: recommendedRulesCount, boldThis }
  );

  const applyConfirmationContent = (
    <Box className='Ext-ManageClassification-confirmationContent'>
      <Typography kind='h2'>{applyConfirmationLabel}</Typography>
      <Typography kind='body2' color='--color-foreground-neutral-medium'>
        {l(
          'Once the rules are applied, the user classification process will begin. The duration may vary depending on the size of the user base.'
        )}
      </Typography>
      <Typography kind='body2' color='--color-foreground-neutral-medium'>
        {l(
          'The system will run a daily process to automatically classify identities based on the defined rules'
        )}
      </Typography>
      {recommendedRulesCount ? (
        <Typography kind='body2' color='--color-foreground-neutral-medium'>
          {unconfirmedRuleText}
        </Typography>
      ) : (
        ''
      )}
    </Box>
  );

  return (
    <div className='Ext-ManageClassification ModalPage-root'>
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showCriticalBanner}
        onCancel={() => setShowCriticalBanner(false)}
        isColonVisible={false}
        autoDismissDelay={30000}
        shouldAutoDismiss
        isFloating
      />
      <ModalPageHeader
        title={l('Review User Classification Rules')}
        subHeadingContent='EXTERNAL USER CLASSIFICATION'
        closeHandler={closeHandler}
      />
      <div className='Ext-ManageClassification-content'>
        {isLoading && (
          <div className='Ext-ManageClassification-content-loader'>
            <Loader kind='loop' format='main' color='brand' size='large' />
          </div>
        )}
        <Typography kind='h2'>
          {recommendedRulesCount && recommendedRulesCount > 0
            ? l(
                'We’ve analyzed your user data and generated classification rules for identifying external users (elevated-risk users). Please review these rules and make any necessary adjustments to ensure accuracy.'
              )
            : confirmedRulesText}
          {!initialRules?.length && !isLoading && !isFetching
            ? l(
                `No classification rules found. Please add rules to define how external users should be identified.`
              )
            : null}
        </Typography>
        <Box className='Ext-ManageClassification-content-ModalIsland'>
          {!isLoading && (
            <Button
              type='button'
              kind='ghost'
              size='medium'
              onClick={() => setIsOverlayOpen(true)}>
              {l('What is a classification rule?')}
            </Button>
          )}
          <ModalIsland
            isShellMode
            isCloseButtonVisible
            HeaderComp={ruleHeaderComp}
            alertBackgroundClassName='Ext-ManageClassification-content-ModalIsland-customAlertBackGround'
            alertTitle={l('Guided User Classification with Rules')}
            alertSubtitle={l(
              'As part of the identity classification process, our system performs a comprehensive scan of all users and their attributes. Using the data gathered, we automatically generate classification rules that define and categorize user types based on their attributes.'
            )}
            onClose={() => setIsOverlayOpen(false)}
            isOpen={isOverlayOpen}
            contentBody={ruleContentBody}
            primaryButton={
              <Button
                type='button'
                kind='outlined'
                size='medium'
                onClick={() => setIsOverlayOpen(false)}>
                {l('Got it!')}
              </Button>
            }
          />
        </Box>
        {initialRules && (
          <RuleTabs
            rules={initialRules}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            refetchRules={refetchRules}
            isRulesfetched={isRulesfetched}
          />
        )}
      </div>
      <FooterActionBar size='large' className='Ext-StepperFooter'>
        <div className='Ext-ManageClassification-footer'>
          <Button
            type='button'
            kind='filled'
            onClick={() => {
              setIsModalOpen(true);
            }}
            className='CertsActionPopover-submtBtn'>
            {applyButtonLabel}
          </Button>
          <ModalIsland
            kind='warning'
            alertTitle={applyConfirmationLabel}
            HeaderComp={applyConfirmationContent}
            onClose={closeModal}
            isOpen={isModalOpen}
            primaryButton={
              <Button
                type='button'
                kind='outlined'
                size='medium'
                isLoading={isRuleUpdating}
                loaderText='Applying Rules'
                isDisabled={isRuleUpdating}
                onClick={applyHandler}>
                {l('Apply')}
              </Button>
            }
            secondaryButton={
              <Button
                type='button'
                kind='ghost'
                size='medium'
                onClick={closeModal}>
                {l('Cancel')}
              </Button>
            }
          />
        </div>
      </FooterActionBar>
    </div>
  );
}

export default ManageClassification;
